"use client";

import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { PRIMER_TM_CALCULATOR_STRINGS } from "@/components/tools/primer-tm-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Conditions,
  checkPrimer,
  gcPercent,
  tmNearestNeighbour,
  tmSaltAdjusted,
  tmWallace,
} from "@/lib/tool-kit/biology-genetics/primer-tm";

export type PrimerStrings = typeof PRIMER_TM_CALCULATOR_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

/** Wallace's rule was fitted to short oligonucleotides; beyond this it overshoots. */
const WALLACE_LIMIT = 14;

export function primerTms(sequence: string, conditions: Conditions) {
  return {
    wallace: tmWallace(sequence),
    saltAdjusted: tmSaltAdjusted(sequence, conditions),
    neighbour: tmNearestNeighbour(sequence, conditions),
  };
}

function degrees(fmt: Fmt, value: number | null, unit: string): string {
  return value === null
    ? "—"
    : `${fmt(value, { maximumFractionDigits: 1 })} ${unit}`;
}

export function PrimerCard({
  heading,
  sequence,
  conditions,
  strings,
  fmt,
}: {
  heading: string;
  sequence: string;
  conditions: Conditions;
  strings: PrimerStrings;
  fmt: Fmt;
}) {
  const tms = primerTms(sequence, conditions);
  const check = checkPrimer(sequence);
  const gc = gcPercent(sequence);
  const methods = [
    {
      name: strings.methodNn,
      value: tms.neighbour?.tm ?? null,
      note: strings.noteNn,
    },
    {
      name: strings.methodSalt,
      value: tms.saltAdjusted,
      note: strings.noteSalt,
    },
    {
      name: strings.methodWallace,
      value: tms.wallace,
      note: strings.noteWallace,
    },
  ];
  const checks = [
    { ok: check.lengthOk, text: strings.checkLength },
    { ok: check.gcOk, text: strings.checkGc },
    { ok: check.clampOk, text: strings.checkClamp },
    {
      ok: check.runOk,
      text: t(strings.checkRuns, { run: fmt(check.longestRun) }),
    },
  ];
  return (
    <div className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <p className="break-all font-mono text-foreground text-sm">
        5′-{sequence}-3′
      </p>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.methodNn}
          value={degrees(fmt, tms.neighbour?.tm ?? null, strings.unitC)}
        />
        <StatCard
          label={strings.length}
          value={`${fmt(sequence.length)} ${strings.unitNt}`}
        />
        <StatCard
          label={strings.gcContent}
          value={`${fmt(gc, { maximumFractionDigits: 1 })}%`}
        />
      </StatGrid>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.methodColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {strings.tmColumn}
              </th>
              <th className={TH} scope="col">
                {strings.noteColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {methods.map((method) => (
              <tr key={method.name}>
                <td className={TD}>{method.name}</td>
                <td className={`${TD} text-right`}>
                  {degrees(fmt, method.value, strings.unitC)}
                </td>
                <td className={`${TD} text-muted-foreground`}>{method.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sequence.length > WALLACE_LIMIT ? (
        <p className={HINT}>
          {t(strings.wallaceWarning, { limit: fmt(WALLACE_LIMIT) })}
        </p>
      ) : null}
      {tms.neighbour ? (
        <p className={HINT}>
          {t(strings.thermo, {
            dh: fmt(tms.neighbour.deltaH, { maximumFractionDigits: 1 }),
            ds: fmt(tms.neighbour.deltaS, { maximumFractionDigits: 1 }),
            dg: fmt(tms.neighbour.deltaG37, { maximumFractionDigits: 1 }),
          })}
          {tms.neighbour.selfComplementary
            ? ` ${strings.selfComplementary}`
            : ""}
        </p>
      ) : null}
      <ul className="space-y-1 text-sm">
        {checks.map((item) => (
          <li
            className={item.ok ? "text-foreground" : "text-destructive"}
            key={item.text}
          >
            <span aria-hidden="true">{item.ok ? "✓ " : "! "}</span>
            <span className="sr-only">
              {item.ok ? strings.pass : strings.warn}:{" "}
            </span>
            {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PairSummary({
  first,
  second,
  strings,
  fmt,
  conditions,
  matches,
}: {
  first: string;
  second: string;
  strings: PrimerStrings;
  fmt: Fmt;
  conditions: Conditions;
  matches: { selfFirst: boolean; selfSecond: boolean; cross: boolean };
}) {
  const a = tmNearestNeighbour(first, conditions);
  const b = tmNearestNeighbour(second, conditions);
  if (a === null || b === null) {
    return null;
  }
  const difference = Math.abs(a.tm - b.tm);
  const anneal = Math.min(a.tm, b.tm) - 5;
  const candidates: (string | null)[] = [
    matches.selfFirst ? strings.dimerSelfFirst : null,
    matches.selfSecond ? strings.dimerSelfSecond : null,
    matches.cross ? strings.dimerCross : null,
  ];
  const warnings = candidates.filter((text): text is string => text !== null);
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.pairHeading}</h3>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.deltaTm}
          value={degrees(fmt, difference, strings.unitC)}
        />
        <StatCard
          label={strings.annealing}
          value={degrees(fmt, anneal, strings.unitC)}
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        {difference <= 2 ? strings.deltaOk : strings.deltaWide}
      </p>
      <p className={HINT}>{strings.annealingNote}</p>
      {warnings.length === 0 ? (
        <p className="text-foreground text-sm">{strings.noDimer}</p>
      ) : (
        warnings.map((text) => <Notice key={text}>{text}</Notice>)
      )}
    </div>
  );
}
